import React from 'react';
import { Box, Typography, Stack, alpha, keyframes, LinearProgress } from '@mui/material';
import { AlertShell, AlertField } from './ui/AlertShell';
import { StatusPill } from './ui/StatusPill';
import { tokens } from '../theme';
import SyncIcon from '@mui/icons-material/SyncRounded';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutlineRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { IStats } from '../shared/types';
import { isWaitingForFunds } from '../utils/botUtils';
import { AlertBotTitle } from './LowGasAlert';

interface BalancingStatusAlertProps {
    stats: IStats;
    isAdmin?: boolean;
}

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

export const BalancingStatusAlert: React.FC<BalancingStatusAlertProps> = ({
    stats,
    isAdmin = false,
}) => {
    // Only show to admins
    if (!isAdmin) return null;

    // Don't show if no balancing step
    if (!stats.currentBalancingStep) return null;

    const subStep = stats.currentBalancingSubStep?.toLowerCase() || '';
    
    // Don't show this alert if WaitingFundsAlert will be shown
    if (isWaitingForFunds(stats)) return null;

    const isWaitingDeposit = subStep.includes('waiting') && subStep.includes('deposit');
    const isProcessing = subStep.includes('processing') || subStep.includes('executing');
    const hasSwapProgress = typeof stats.balancingSwapProgressPct === 'number';
    const hasSwapPair = Boolean(stats.balancingSwapTokenIn && stats.balancingSwapTokenOut);
    const swapProgressPct = Math.max(0, Math.min(100, stats.balancingSwapProgressPct ?? 0));

    // Balancing is work in progress (gold) in every sub-step, including the bot's own
    // deposits in transit. External funds that need an operator go to WaitingFundsAlert (amber).
    const accentColor = tokens.gold;

    // Select icon based on status
    const StatusIcon = isWaitingDeposit
        ? PauseCircleOutlineIcon
        : isProcessing
            ? SyncIcon
            : CheckCircleOutlineIcon;

    // Format step info
    const formatStepType = (step: string): string => {
        // Capitalize and clean up step names
        // Split camelCase only; all-caps step ids (DERIBIT, WRAP) stay as they are.
        return step.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase()).trim();
    };

    const formatAmount = (amount?: number): string => {
        if (typeof amount !== 'number') return '';
        const maximumFractionDigits = Math.abs(amount) >= 1 ? 2 : 6;
        return amount.toLocaleString(undefined, { maximumFractionDigits });
    };
    const hasBalancingAmount = Boolean(stats.balancingTokenAmount && stats.balancingTokenName);

    const fields: AlertField[] = [
        { label: 'Step', value: formatStepType(stats.currentBalancingStep) },
    ];
    if (stats.currentBalancingSubStep) {
        fields.push({ label: 'Current action', value: stats.currentBalancingSubStep });
    }
    if (hasSwapPair) {
        fields.push({
            label: 'Converting',
            value: `${stats.balancingSwapTokenIn} → ${stats.balancingSwapTokenOut}`,
            color: accentColor,
        });
    }
    if (hasBalancingAmount) {
        fields.push({
            label: 'Amount',
            value: `${formatAmount(stats.balancingTokenAmount)} ${stats.balancingTokenName}`,
            color: accentColor,
        });
    }
    if (stats.balancingClaimDate) {
        fields.push({
            label: 'Claim date',
            value: new Date(stats.balancingClaimDate).toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }),
        });
    }
    if (stats.balancingUSD > 0 && !hasBalancingAmount) {
        fields.push({
            label: 'Value',
            value: `$${stats.balancingUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        });
    }

    return (
        <AlertShell
            color={accentColor}
            icon={
                <StatusIcon
                    sx={{
                        animation: isProcessing ? `${rotate} 2s linear infinite` :
                                   isWaitingDeposit ? `${pulse} 2s ease-in-out infinite` :
                                   'none',
                    }}
                />
            }
            title={<AlertBotTitle botId={stats.botName} />}
            subtitle="Balancing in progress"
            fields={fields}
            trailing={
                <StatusPill
                    tone="brand"
                    label={isWaitingDeposit ? 'Waiting' : isProcessing ? 'Processing' : 'Active'}
                    size="sm"
                    pulsing={isProcessing}
                />
            }
        >
            {hasSwapProgress && (
                <Box sx={{ mt: fields.length ? 1.75 : 0, maxWidth: { md: 520 }, mx: { md: 'auto' } }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography variant="caption" color="text.secondary">
                            Swap progress
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 650 }}>
                            {swapProgressPct.toFixed(1)}%
                            {typeof stats.balancingSwapRemaining === 'number' && stats.balancingSwapTokenIn && (
                                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    {` · ${formatAmount(stats.balancingSwapRemaining)} ${stats.balancingSwapTokenIn} left`}
                                </Box>
                            )}
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={swapProgressPct}
                        sx={{
                            height: 6,
                            bgcolor: alpha(accentColor, 0.15),
                            '& .MuiLinearProgress-bar': { bgcolor: accentColor },
                        }}
                    />
                </Box>
            )}
        </AlertShell>
    );
};
